// src/SpinPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { performSpin, getFeaturedItems } from './api';
import { useAuth } from './AuthContext';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import useSound from 'use-sound'; // <-- Import useSound

// --- Material UI Imports ---
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import { styled, keyframes, alpha } from '@mui/system';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory';

// --- Sound Files (assuming they are in public/sounds) ---
// const spinStartSound = '/sounds/spin_start.mp3'; // <-- REMOVED: Replaced by ticks
const spinRevealSound = '/sounds/spin_reveal.mp3';
const spinTickSound = '/sounds/spin_tick.mp3'; // <-- NEW: Sound for item ticks

// --- Helper Function (getSteamImageUrl) ---
const getSteamImageUrl = (iconUrlPath, sizeSuffix = '/150fx150f') => { // Default to larger size for won item
    // ... (keep existing function)
    if (!iconUrlPath) return '';
    if (iconUrlPath.startsWith('http')) {
        if (iconUrlPath.includes('community.cloudflare.steamstatic.com/economy/image/') && !iconUrlPath.match(/\/\d+fx\d+f/)) {
            return `${iconUrlPath}${sizeSuffix}`;
        }
        return iconUrlPath; // Return as is if size is already specified or not a steam image
    }
    if (iconUrlPath.startsWith('//')) {
        return `https:${iconUrlPath}${sizeSuffix}`;
    }
    if (iconUrlPath.startsWith('ttps://')) {
        return `h${iconUrlPath}${sizeSuffix}`;
    }
    return `https://community.cloudflare.steamstatic.com/economy/image/${iconUrlPath}${sizeSuffix}`;
};

// --- NEW: Helper function to format wear ---
const formatWear = (wearValue) => {
    if (wearValue === null || wearValue === undefined || wearValue === '') {
        return 'N/A';
    }
    // Example: Format to 6 decimal places if it's a number
    if (typeof wearValue === 'number') {
        return wearValue.toFixed(6);
    }
    // Otherwise, return the value as is (might be a string like "Factory New")
    return String(wearValue);
};
// --- END NEW ---


// --- Constants for Animation ---
// ... (keep existing constants)
const ITEM_WIDTH = 160;
const ITEM_MARGIN_X = 8;
const TOTAL_ITEM_SPACE = ITEM_WIDTH + ITEM_MARGIN_X * 2;
const NUM_VISIBLE_ITEMS_APPROX = 6;
const CONTAINER_PADDING_Y = 15;
const ROULETTE_HEIGHT = ITEM_WIDTH + CONTAINER_PADDING_Y * 2;
const WIGGLE_DURATION = 700;
const SPIN_ANIMATION_DURATION = 7500;
const DISPLAY_AREA_HEIGHT = ROULETTE_HEIGHT * 2.5;
const WIN_CARD_ANIMATION_DURATION = 800;

// --- Constants for Featured Carousel ---
// ... (keep existing constants)
const FEATURED_ITEM_WIDTH = 110;
const FEATURED_ITEM_MARGIN_X = 8;
const FEATURED_TOTAL_ITEM_SPACE = FEATURED_ITEM_WIDTH + FEATURED_ITEM_MARGIN_X * 2;
const FEATURED_CAROUSEL_HEIGHT = FEATURED_ITEM_WIDTH + 10;

// --- Placeholder Item ---
// ... (keep existing placeholder)
const placeholderItem = {
    name: '?',
    name_color: '888888',
    icon_url: '',
    assetid: 'placeholder',
    classid: 'placeholder',
    instanceid: 'placeholder',
    wear: null, // Add wear placeholder
};

// --- Styled Components & Keyframes ---
// ... (keep existing styled components and keyframes)
const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const glowAnimation = (color) => keyframes`
  0% { box-shadow: 0 0 10px 4px ${alpha('#' + (color || 'D2D2D2'), 0.4)}; }
  50% { box-shadow: 0 0 25px 10px ${alpha('#' + (color || 'D2D2D2'), 0.7)}; }
  100% { box-shadow: 0 0 10px 4px ${alpha('#' + (color || 'D2D2D2'), 0.4)}; }
`;

const wiggle = keyframes`
  0%, 100% { transform: rotate(0deg) scale(1); }
  20% { transform: rotate(-5deg) scale(1.05) translateX(-5px); }
  40% { transform: rotate(5deg) scale(1.05) translateX(5px); }
  60% { transform: rotate(-3deg) scale(1.03) translateX(-3px); }
  80% { transform: rotate(3deg) scale(1.03) translateX(3px); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const popIn = keyframes`
  0% { opacity: 0; transform: scale(0.3) translateY(80px) rotate(-15deg); }
  60% { opacity: 1; transform: scale(1.15) translateY(-10px) rotate(5deg); }
  80% { transform: scale(0.95) translateY(0px) rotate(-2deg); }
  100% { opacity: 1; transform: scale(1) translateY(0px) rotate(0deg); }
`;


const WonItemCard = styled(Card)(({ theme, nameColor }) => ({
    // ... (keep existing styles)
    maxWidth: 420,
    margin: '40px auto',
    textAlign: 'center',
    border: `4px solid #${nameColor || 'D2D2D2'}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    boxShadow: `0 0 35px 10px ${alpha('#' + (nameColor || 'D2D2D2'), 0.6)}`,
    animation: `${glowAnimation(nameColor)} 2.5s ease-in-out infinite`,
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(6px)',
    overflow: 'visible',
    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-12px) scale(1.03)',
        boxShadow: `0 20px 50px 12px ${alpha('#' + (nameColor || 'D2D2D2'), 0.8)}`,
    }
}));

// --- FeaturedItemsCarousel Component ---
const FeaturedItemsCarousel = React.memo(({ items, loading, error }) => {
    // ... (loading/error/empty checks remain the same)
    if (loading) { return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: FEATURED_CAROUSEL_HEIGHT, my: 2 }}><CircularProgress size={30} /><Typography sx={{ ml: 2 }} variant="body1" color="text.secondary">Loading items...</Typography></Box>; }
    if (error) { return <Alert severity="warning" sx={{ my: 2, justifyContent: 'center' }}>{error}</Alert>; }
    if (!items || items.length === 0) { return null; }

    let displayItems = items || [];
    while (displayItems.length > 0 && displayItems.length < 15) { displayItems = [...displayItems, ...items]; }
    displayItems = displayItems.slice(0, 30);
    const totalItemWidth = FEATURED_ITEM_WIDTH + FEATURED_ITEM_MARGIN_X * 2;
    const animationDuration = displayItems.length * 1.5;

    // --- MODIFICATION: Add wear to Tooltip title ---
    const getFeaturedTooltipTitle = (item) => (
        <React.Fragment>
            <Typography color="inherit" sx={{ fontWeight: 'bold' }}>{item.name || 'Featured Item'}</Typography>
            {item.wear !== undefined && item.wear !== null && (
                <Typography variant="caption" display="block">Wear: {formatWear(item.wear)}</Typography>
            )}
        </React.Fragment>
    );
    // --- END MODIFICATION ---

    return (
        <Box sx={{ width: '100%', overflow: 'hidden', py: 2, position: 'relative', maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)', WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)', }}>
            <Box sx={{ display: 'flex', width: `calc(${totalItemWidth * displayItems.length * 2}px)`, animation: `${scroll} ${animationDuration}s linear infinite`, '&:hover': { animationPlayState: 'paused', }, }}>
                {[...displayItems, ...displayItems].map((item, index) => (
                    // --- MODIFICATION: Use new tooltip title function ---
                    <Tooltip title={getFeaturedTooltipTitle(item)} key={`${item.assetid || 'featured'}-${index}`} placement="top">
                        {/* --- END MODIFICATION --- */}
                        <Card sx={{ flexShrink: 0, width: `${FEATURED_ITEM_WIDTH}px`, height: `${FEATURED_ITEM_WIDTH}px`, mx: `${FEATURED_ITEM_MARGIN_X}px`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 0.5, bgcolor: alpha('#000', 0.3), border: `2px solid #${item.name_color || '666'}`, borderRadius: '4px', overflow: 'hidden', transition: 'transform 0.2s ease', '&:hover': { transform: 'scale(1.05)', zIndex: 1, } }}>
                            <CardMedia component="img" sx={{ maxHeight: '65%', maxWidth: '90%', objectFit: 'contain', mb: 'auto' }} image={getSteamImageUrl(item.icon_url, '/96fx96f')} alt={item.name || '...'} loading="lazy" />
                            {/* <Typography variant="caption" noWrap sx={{ color: `#${item.name_color || 'D2D2D2'}`, fontWeight: 'bold', width: '100%', mt: 0.5, px: 0.5, fontSize: '0.6rem', textAlign: 'center', }}>{item.name || '...'}</Typography> */} {/* <-- Removed original */}
                            {/* --- MODIFICATION: Add text shadow for outline --- */}
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{
                                    color: `#${item.name_color || 'D2D2D2'}`,
                                    fontWeight: 'bold', width: '100%', mt: 0.5, px: 0.5, fontSize: '0.6rem', textAlign: 'center',
                                    textShadow: '1px 1px 0 #FFF, -1px -1px 0 #FFF, 1px -1px 0 #FFF, -1px 1px 0 #FFF, 0px 1px 0 #FFF, 0px -1px 0 #FFF, 1px 0px 0 #FFF, -1px 0px 0 #FFF' // White outline
                                }}
                            >
                            </Typography>
                            {/* --- END MODIFICATION --- */}
                        </Card>
                    </Tooltip>
                ))}
            </Box>
        </Box>
    );
});

// --- SpinAnimation Component ---
const SpinAnimation = React.memo(({ reelItems, targetIndex, duration = SPIN_ANIMATION_DURATION, isAnimating, playTickSound }) => {
    const reelRef = useRef(null);
    const animationFrameIdRef = useRef(null);
    const lastTickedItemIndexRef = useRef(-1);


    useEffect(() => {
        // ... (keep existing useEffect logic)
        const reelElement = reelRef.current;
        if (!reelElement) return;

        if (isAnimating && targetIndex >= 0) {
            reelElement.style.transition = 'none';
            reelElement.style.transform = 'translateX(0px)';
            void reelElement.offsetWidth; // Force reflow

            const calculateFinalPosition = () => {
                const container = reelElement.parentElement;
                if (!container) return 0;
                const containerWidth = container.offsetWidth;
                const centerPointOfContainer = containerWidth / 2;
                const targetItemLeftEdge = targetIndex * TOTAL_ITEM_SPACE;
                const desiredLeftOffset = centerPointOfContainer - (TOTAL_ITEM_SPACE / 2);
                const randomOffset = (Math.random() - 0.5) * (TOTAL_ITEM_SPACE * 0.15);
                return -(targetItemLeftEdge - desiredLeftOffset) + randomOffset;
            };
            const finalTranslateX = calculateFinalPosition();

            requestAnimationFrame(() => { // Ensure this is scheduled after reflow
                if (reelRef.current) {
                    reelRef.current.style.transition = `transform ${duration}ms cubic-bezier(0.3, 0.7, 0.1, 1.05)`;
                    reelRef.current.style.transform = `translateX(${finalTranslateX}px)`;
                }
            });

            const startTime = performance.now();
            lastTickedItemIndexRef.current = -1; // Reset before starting ticks

            const animateTicks = (timestamp) => {
                if (!reelRef.current || !isAnimating) { // Check isAnimating for early exit
                    if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
                    lastTickedItemIndexRef.current = -1;
                    return;
                }

                const elapsedTime = timestamp - startTime;
                if (elapsedTime >= duration) {
                    if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
                    lastTickedItemIndexRef.current = -1;
                    return;
                }

                const style = window.getComputedStyle(reelRef.current);
                const matrix = new DOMMatrixReadOnly(style.transform);
                const currentTranslateX = matrix.m41;

                const container = reelRef.current.parentElement;
                if (!container) {
                    if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current);
                    lastTickedItemIndexRef.current = -1;
                    return;
                }

                const containerWidth = container.offsetWidth;
                const centerPointOfContainer = containerWidth / 2;

                // Position of the marker line on the reel strip (from the left of the reel strip)
                const markerPositionOnReel = centerPointOfContainer - currentTranslateX;

                // Index of the item whose left edge is at or before the marker
                const currentItemIndex = Math.floor(markerPositionOnReel / TOTAL_ITEM_SPACE);

                if (currentItemIndex >= 0 && currentItemIndex < reelItems.length && currentItemIndex !== lastTickedItemIndexRef.current) {
                    if (playTickSound) {
                        playTickSound();
                    }
                    lastTickedItemIndexRef.current = currentItemIndex;
                }
                animationFrameIdRef.current = requestAnimationFrame(animateTicks);
            };
            animationFrameIdRef.current = requestAnimationFrame(animateTicks);

        } else if (!isAnimating && targetIndex >= 0) {
            if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current); // Stop ticks
            const calculateFinalPosition = () => {
                const container = reelElement.parentElement;
                if (!container) return 0;
                const containerWidth = container.offsetWidth;
                const centerPointOfContainer = containerWidth / 2;
                const targetItemLeftEdge = targetIndex * TOTAL_ITEM_SPACE;
                const desiredLeftOffset = centerPointOfContainer - (TOTAL_ITEM_SPACE / 2);
                return -(targetItemLeftEdge - desiredLeftOffset); // No random offset when snapping
            };
            const finalTranslateX = calculateFinalPosition();
            if (reelElement.style.transform !== `translateX(${finalTranslateX}px)`) {
                reelElement.style.transition = 'none';
                reelElement.style.transform = `translateX(${finalTranslateX}px)`;
            }
            lastTickedItemIndexRef.current = -1;
        } else if (targetIndex < 0) {
            if (animationFrameIdRef.current) cancelAnimationFrame(animationFrameIdRef.current); // Stop ticks
            reelElement.style.transition = 'none';
            reelElement.style.transform = `translateX(0px)`;
            lastTickedItemIndexRef.current = -1;
        }

        return () => { // Cleanup function for useEffect
            if (animationFrameIdRef.current) {
                cancelAnimationFrame(animationFrameIdRef.current);
            }
            lastTickedItemIndexRef.current = -1;
        };
    }, [reelItems, targetIndex, duration, isAnimating, playTickSound]);

    // --- MODIFICATION: Update renderItem to include wear ---
    const renderItem = useCallback((item, index) => (
        <Tooltip
            title={
                <React.Fragment>
                    <Typography color="inherit" sx={{ fontWeight: 'bold' }}>{item.name || '...'}</Typography>
                    {item.wear !== undefined && item.wear !== null && (
                        <Typography variant="caption" display="block">Wear: {formatWear(item.wear)}</Typography>
                    )}
                </React.Fragment>
            }
            placement="top"
            key={`${item.assetid || 'item'}-${index}`}
        >
            <Paper
                elevation={3}
                sx={(theme) => ({
                    flexShrink: 0,
                    width: `${ITEM_WIDTH}px`,
                    height: `${ITEM_WIDTH}px`,
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '8px',
                    mx: `${ITEM_MARGIN_X}px`,
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    border: `3px solid #${item.name_color || '777'}`,
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '8px',
                    transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
                    '&:hover': {
                        transform: 'scale(1.03)',
                        zIndex: 1,
                        boxShadow: theme.shadows[4],
                    }
                })}
            >
                {item.icon_url ? (
                    <CardMedia
                        component="img"
                        sx={{
                            maxHeight: '65%',
                            maxWidth: '90%',
                            objectFit: 'contain',
                            mb: 'auto',
                            filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.5))'
                        }}
                        image={getSteamImageUrl(item.icon_url, '/150fx150f')}
                        alt={item.name || '...'}
                        loading="lazy"
                    />
                ) : (
                    <Box sx={{ height: '65%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                        <Typography variant="h3">?</Typography>
                    </Box>
                )}
                <Typography
                    variant="caption"
                    noWrap
                    sx={{
                        color: `#${item.name_color || 'D2D2D2'}`,
                        fontWeight: 'bold',
                        width: '100%',
                        mt: 1,
                        px: 0.5,
                        fontSize: '0.75rem'
                    }}
                >
                    {item.name || '...'}
                </Typography>
                {/* Optional: Display wear directly on the card if space allows */}
                {/* <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary', mt: 0.2 }}>
                    {formatWear(item.wear)}
                </Typography> */}
            </Paper>
        </Tooltip>
    ), []);
    // --- END MODIFICATION ---

    return (
        <Box sx={{
            // ... (keep existing styles for the container)
            width: '100%',
            maxWidth: `${TOTAL_ITEM_SPACE * NUM_VISIBLE_ITEMS_APPROX}px`,
            height: `${ROULETTE_HEIGHT}px`,
            border: (theme) => `2px solid ${alpha(theme.palette.divider, 0.7)}`,
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            margin: '20px auto',
            padding: `${CONTAINER_PADDING_Y}px 0`,
            boxSizing: 'content-box',
            background: (theme) => `linear-gradient(to bottom, ${alpha(theme.palette.grey[900], 0.7)}, ${alpha(theme.palette.grey[800], 0.8)}, ${alpha(theme.palette.grey[900], 0.7)})`,
            backdropFilter: 'blur(4px)',
            boxShadow: 'inset 0 0 15px rgba(0,0,0,0.6)',
        }}>
            {/* Marker */}
            <Box sx={{
                position: 'absolute',
                top: '-5px',
                bottom: '-5px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '5px',
                backgroundColor: (theme) => theme.palette.primary.main,
                zIndex: 2,
                boxShadow: (theme) => `0 0 20px 5px ${alpha(theme.palette.primary.light, 0.8)}`,
                borderRadius: '3px',
            }} />
            {/* Reel */}
            <Box ref={reelRef} sx={{ display: 'flex', height: '100%', willChange: 'transform' }}>
                {reelItems.map(renderItem)}
            </Box>
        </Box>
    );
});


// --- Main SpinPage Component ---
function SpinPage() {
    const [spinPhase, setSpinPhase] = useState('idle'); // 'idle', 'wiggling', 'spinning', 'showingResult'
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [wonItem, setWonItem] = useState(null);
    const [reelAnimationItems, setReelAnimationItems] = useState(() => Array(60).fill(placeholderItem));
    const [targetItemIndex, setTargetItemIndex] = useState(-1);
    const [error, setError] = useState(null);
    const { user, refreshUser } = useAuth();
    const spinTimeoutRef = useRef(null);
    const wiggleTimeoutRef = useRef(null);
    const navigate = useNavigate();

    const [featuredItems, setFeaturedItems] = useState([]);
    const [featuredLoading, setFeaturedLoading] = useState(false);
    const [featuredError, setFeaturedError] = useState(null);

    // --- Initialize Sounds ---
    // const [playSpinStart] = useSound(spinStartSound, { volume: 0.5 }); // <-- REMOVED: Replaced by ticks
    const [playSpinReveal] = useSound(spinRevealSound, { volume: 0.6 });
    const [playSpinTick] = useSound(spinTickSound, { volume: 0.35 }); // <-- NEW: Tick sound

    // Set Document Title
    useEffect(() => {
        document.title = 'SpinnerCS - Spin';
    }, []);

    // Fetch featured items
    useEffect(() => {
        // ... (keep existing fetchFeatured logic)
        const fetchFeatured = async () => {
            setFeaturedLoading(true);
            setFeaturedError(null);
            try {
                const items = await getFeaturedItems();
                setFeaturedItems(items || []);
            } catch (err) {
                const errMsg = err.response?.data?.message || err.response?.data || err.message || 'Failed to load featured items.';
                setFeaturedError(errMsg);
            } finally {
                setFeaturedLoading(false);
            }
        };
        fetchFeatured();
    }, []);

    // Cleanup timeouts
    useEffect(() => {
        // ... (keep existing cleanup logic)
        return () => {
            if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
            if (wiggleTimeoutRef.current) clearTimeout(wiggleTimeoutRef.current);
        };
    }, []);

    // Reset state function
    const resetSpinState = useCallback(() => {
        // ... (keep existing reset logic)
        setSpinPhase('idle');
        setIsApiLoading(false);
        setIsAnimating(false);
        setWonItem(null);
        setTargetItemIndex(-1);
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);
        if (wiggleTimeoutRef.current) clearTimeout(wiggleTimeoutRef.current);
    }, []);

    // Handle spin logic
    const handleSpin = async () => {
        // ... (keep existing spin logic, including checks and API call)
        if (!user?.isAuthenticated) {
            setError("Please log in.");
            return;
        }
        if ((user?.spinCount ?? 0) <= 0) {
            navigate('/offers'); // Redirect to offers page
            return;
        }
        if (spinPhase !== 'idle') {
            setError("Spin already in progress.");
            return;
        }

        setError(null);
        setSpinPhase('wiggling'); // Start wiggling

        if (wiggleTimeoutRef.current) clearTimeout(wiggleTimeoutRef.current);
        if (spinTimeoutRef.current) clearTimeout(spinTimeoutRef.current);

        // Wait for wiggle animation to mostly finish before calling API
        wiggleTimeoutRef.current = setTimeout(async () => {
            setSpinPhase('spinning');
            setIsApiLoading(true);
            // playSpinStart(); // <-- REMOVED: Ticks will handle sound during spin

            try {
                const result = await performSpin();
                const { wonItem: finalWonItem, animationItems: apiAnimationItems } = result;

                if (!finalWonItem || !apiAnimationItems || apiAnimationItems.length === 0) {
                    throw new Error("Invalid spin result data received.");
                }

                setIsApiLoading(false);

                const reelLength = 60;
                const winningIndexInReel = reelLength - 7;

                const preparedReel = [];
                // Ensure the visual winning item includes wear if available
                const visualWinningItem = apiAnimationItems.find(item =>
                    item.assetid === finalWonItem.originalAssetId &&
                    item.classid === finalWonItem.classId &&
                    item.instanceid === finalWonItem.instanceId
                ) || {
                    ...finalWonItem,
                    assetid: finalWonItem.originalAssetId,
                    classid: finalWonItem.classId,
                    instanceid: finalWonItem.instanceId,
                    name: finalWonItem.name,
                    name_color: finalWonItem.nameColor,
                    icon_url: finalWonItem.iconUrl,
                    wear: finalWonItem.wear, // Add wear here
                };

                for (let i = 0; i < reelLength; i++) {
                    if (i === winningIndexInReel) {
                        preparedReel.push(visualWinningItem);
                    } else {
                        // Ensure other items also have wear if available
                        const itemIndex = (i * 3 + winningIndexInReel) % apiAnimationItems.length;
                        preparedReel.push(apiAnimationItems[itemIndex]);
                    }
                }

                setWonItem(finalWonItem); // This already includes wear from the API response
                setReelAnimationItems(preparedReel);
                setTargetItemIndex(winningIndexInReel);
                setIsAnimating(true);

                spinTimeoutRef.current = setTimeout(() => {
                    setIsAnimating(false);
                    setSpinPhase('showingResult');
                    playSpinReveal(); // <-- Play win sound
                }, SPIN_ANIMATION_DURATION + 150);

            } catch (err) {
                const errorMessage = err.response?.data?.message || err.response?.data || err.message || 'Spin failed.';
                setError(errorMessage);
                console.error("Spin Error:", err.response || err);
                setIsApiLoading(false);
                resetSpinState();
            } finally {
                if (refreshUser) {
                    try { await refreshUser(); }
                    catch (refreshErr) {
                        console.error("Failed to refresh user data:", refreshErr);
                        setError(prev => prev ? `${prev} (Failed to update spin count)` : 'Failed to update spin count.');
                    }
                }
            }
        }, WIGGLE_DURATION - 100);
    };

    // Handle result confirmation
    const handleResultConfirm = () => {
        // ... (keep existing confirm logic)
        resetSpinState();
    };

    // Auth check
    if (!user?.isAuthenticated) {
        // ... (keep existing auth check)
        return <Alert severity="info" sx={{ mt: 4 }}>Please log in to spin.</Alert>;
    }

    const hasSpins = (user?.spinCount ?? 0) > 0;
    const isCaseClickable = spinPhase === 'idle';

    return (
        <Box sx={{ maxWidth: 'lg', margin: 'auto', px: 2, py: 4 }}>
            <Paper elevation={5} sx={{ p: { xs: 2, sm: 4, md: 5 }, borderRadius: '20px', backgroundColor: theme => alpha(theme.palette.background.paper, 0.9), backdropFilter: 'blur(10px)' }}>
                <Stack spacing={4} alignItems="center">

                    {/* Header section */}
                    {/* ... (keep existing header section) ... */}
                    <Box sx={{ textAlign: 'center', width: '100%', maxWidth: '750px' }}>
                        <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                            <Chip
                                icon={<ConfirmationNumberIcon />}
                                label={`Spins: ${user?.spinCount ?? 0}`}
                                color="primary"
                                size="medium"
                                sx={{ fontWeight: 'bold', fontSize: '1rem', p: 0.5 }}
                            />
                            <Button
                                variant="outlined"
                                color="secondary"
                                size="small"
                                component={RouterLink}
                                to="/offers"
                                startIcon={<AddCircleOutlineIcon />}
                            >
                                Get more spins
                            </Button>
                        </Stack>
                        <Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: 600, color: 'text.primary', }}>
                            Open the case for a chance to win awesome CS2 items!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
                            Including Skins, Agents, Cases, Knives, or Gloves.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 3 }} justifyContent="center" alignItems="center" sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <CheckCircleOutlineIcon color="success" fontSize="small" />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>Guaranteed Win Every Spin!</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <InventoryIcon color="info" fontSize="small" />
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>Items Added to Site Inventory</Typography>
                            </Box>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            (Withdraw items to your Steam account from your inventory)
                        </Typography>
                    </Box>

                    {/* Central Display Area */}
                    <Box sx={{ my: 3, width: '100%', height: `${DISPLAY_AREA_HEIGHT}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', perspective: '1200px', }}>
                        {/* Case Image */}
                        <Box component="img" src="/case_512.webp" alt="Spin Case - Click to Open" onClick={handleSpin} sx={{ /* ... keep styles ... */ maxWidth: { xs: '90%', sm: '80%', md: '70%' }, maxHeight: '100%', height: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.6))', animation: spinPhase === 'wiggling' ? `${wiggle} ${WIGGLE_DURATION}ms ease-in-out` : spinPhase === 'idle' ? `${float} 5s ease-in-out infinite` : 'none', opacity: (spinPhase === 'spinning' || spinPhase === 'showingResult') ? 0 : 1, transform: (spinPhase === 'spinning' || spinPhase === 'showingResult') ? 'scale(0.8) translateY(20px)' : 'scale(1) translateY(0px)', transition: 'opacity 0.4s ease-out, transform 0.4s ease-out', position: 'absolute', zIndex: (spinPhase === 'idle' || spinPhase === 'wiggling') ? 1 : -1, cursor: isCaseClickable ? 'pointer' : 'default', '&:hover': isCaseClickable ? { transform: 'scale(1.04) translateY(-5px)', filter: 'drop-shadow(0 12px 25px rgba(0,0,0,0.7))', } : {}, }} />

                        {/* "Out of spins" Overlay */}
                        {!hasSpins && spinPhase === 'idle' && (<Box sx={{ /* ... keep styles ... */ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 2, pointerEvents: 'none', textAlign: 'center', backgroundColor: alpha('#000000', 0.7), borderRadius: '15px', p: 2, }}> <Typography variant="h4" component="div" sx={{ color: 'white', fontWeight: 'bold', textShadow: '2px 2px 5px rgba(0,0,0,0.8)', mb: 2, }}> You're out of spins! </Typography> <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/offers')} startIcon={<AddCircleOutlineIcon />} sx={{ pointerEvents: 'auto', mt: 1, fontWeight: 'bold', }}> Get More Spins </Button> </Box>)}

                        {/* Roulette Animation */}
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: spinPhase === 'spinning' ? 1 : 0, transform: spinPhase === 'spinning' ? 'scale(1)' : 'scale(0.9)', transition: 'opacity 0.4s ease-in, transform 0.4s ease-in', position: 'absolute', zIndex: spinPhase === 'spinning' ? 1 : -1, }}>
                            {(spinPhase === 'spinning' || spinPhase === 'wiggling' || (spinPhase === 'showingResult' && isAnimating)) && (
                                <SpinAnimation
                                    reelItems={reelAnimationItems}
                                    targetIndex={targetItemIndex}
                                    isAnimating={isAnimating}
                                    duration={SPIN_ANIMATION_DURATION}
                                    playTickSound={playSpinTick} // <-- Pass tick sound player
                                />
                            )}
                        </Box>

                        {/* Final Result Display */}
                        <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: spinPhase === 'showingResult' ? 1 : 0, transition: `opacity ${WIN_CARD_ANIMATION_DURATION / 2}ms ease-out ${WIN_CARD_ANIMATION_DURATION / 3}ms`, position: 'absolute', zIndex: spinPhase === 'showingResult' ? 2 : -1, animation: spinPhase === 'showingResult' ? `${popIn} ${WIN_CARD_ANIMATION_DURATION}ms cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards` : 'none', pointerEvents: spinPhase === 'showingResult' ? 'auto' : 'none', }}>
                            {spinPhase === 'showingResult' && wonItem && (
                                <WonItemCard nameColor={wonItem.nameColor}>
                                    <CardMedia component="img" height="220" image={getSteamImageUrl(wonItem.iconUrl, '/300fx300f')} alt={wonItem.name || 'Won Item'} sx={{ objectFit: 'contain', padding: 3, mt: 2, mb: 1, backgroundColor: 'rgba(0,0,0,0.25)', filter: 'drop-shadow(3px 3px 5px rgba(0,0,0,0.5))' }} />
                                    <CardContent sx={{ p: 2, pb: 1 }}>
                                        <Typography gutterBottom variant="h3" component="div" sx={{ color: 'success.light', fontWeight: 'bold', textShadow: '1px 1px 3px rgba(0,0,0,0.5)' }}>
                                            You Won!
                                        </Typography>
                                        <Typography variant="h4" sx={{ color: `#${wonItem.nameColor || 'inherit'}`, fontWeight: 'bold', mt: 1.5, textShadow: '1px 1px 2px rgba(0,0,0,0.4)' }}>
                                            {wonItem.name || 'Unknown Item'}
                                        </Typography>
                                        {/* --- MODIFICATION START --- */}
                                        {/* Display Wear without prefix and larger */}
                                        {wonItem.wear !== undefined && wonItem.wear !== null && (
                                            <Typography
                                                variant="h6" // Use a larger variant like h6 or body1 with sx prop
                                                color="text.secondary"
                                                sx={{
                                                    mt: 1,
                                                    fontWeight: 'medium', // Optional: adjust weight
                                                    // fontSize: '1.1rem' // Or use fontSize directly
                                                }}
                                            >
                                                {formatWear(wonItem.wear)}
                                            </Typography>
                                        )}
                                        {/* Hide Acquired Date */}
                                        {/* {wonItem.acquiredDate && (
                                            <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
                                                Acquired: {new Date(wonItem.acquiredDate).toLocaleString()}
                                            </Typography>
                                        )} */}
                                        {/* --- MODIFICATION END --- */}
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'center', p: 3, pt: 1.5 }}>
                                        <Button variant="contained" color="success" size="large" onClick={handleResultConfirm} sx={{ fontWeight: 'bold', px: 4 }}>
                                            Add to inventory
                                        </Button>
                                    </CardActions>
                                </WonItemCard>
                            )}
                        </Box>
                    </Box>

                    {/* Click Indication Text */}
                    {isCaseClickable && hasSpins && spinPhase === 'idle' && (<Typography variant="h5" sx={{ mt: -1, mb: 1, animation: `${float} 3s ease-in-out infinite`, fontWeight: 'bold', color: 'primary.light', textShadow: '1px 1px 3px rgba(0,0,0,0.4)', }}> Click the case to open! </Typography>)}

                    {/* Spin Controls & Feedback */}
                    <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: '600px' }}>
                        {error && (<Alert severity="error" variant="filled" sx={{ width: '100%', justifyContent: 'center' }}> {error} </Alert>)}
                        {isApiLoading && (<Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', mt: 2 }}> <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} /> <Typography variant="body1">Requesting spin...</Typography> </Box>)}
                    </Stack>

                    {/* Featured Items Carousel */}
                    <Box sx={{ width: '100%', mt: 4, mb: 2 }}>
                        <Typography variant="overline" display="block" align="center" color="text.secondary" gutterBottom>
                            Rare chance to contain one of these items
                        </Typography>
                        <FeaturedItemsCarousel
                            items={featuredItems}
                            loading={featuredLoading}
                            error={featuredError}
                        />
                        <Divider sx={{ width: '80%', borderColor: theme => alpha(theme.palette.divider, 0.5), mt: 3, mx: 'auto' }} />
                    </Box>

                </Stack>
            </Paper>
        </Box>
    );
}

export default SpinPage;
